import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, UserPlus } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import PublicHeader from './PublicHeader';
import './SignUpPage.css';

const STEPS = [
  { num: 1, title: 'Create custom client and window types', description: 'Set up customer types and window pricing your way.' },
  { num: 2, title: 'Fast on-site bidding', description: 'No math, calculator, or app switching. Quote in one place.' },
  { num: 3, title: 'Save as PDF or sync to Jobber', description: 'Send a PDF quote or push directly to Jobber.' },
];

const VALID_ACCESS_CODE = 'WQM-2025-X7K9-M3N8-P2Q5';

const SignUpPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { signup, currentUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Squeekit - Sign Up';
  }, []);

  useEffect(() => {
    if (currentUser) {
      const completed = localStorage.getItem(`onboarding_completed_${currentUser.uid}`);
      navigate(completed ? '/' : '/onboarding', { replace: true });
    }
  }, [currentUser, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (accessCode !== VALID_ACCESS_CODE) {
      return setError('Invalid access code. Please contact administrator for access.');
    }
    if (password !== confirmPassword) {
      return setError('Passwords do not match');
    }

    try {
      setLoading(true);
      await signup(email, password);
      navigate('/onboarding', { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="signup-page">
      <PublicHeader />
      <div className="signup-page__inner">
        <section className="signup-page__how">
          <h2 className="signup-page__how-title">How it works</h2>
          <ol className="signup-page__steps">
            {STEPS.map((step) => (
              <li key={step.num} className="signup-page__step">
                <span className="signup-page__step-num">{step.num}</span>
                <div>
                  <h3 className="signup-page__step-title">{step.title}</h3>
                  <p className="signup-page__step-desc">{step.description}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        <section className="signup-page__form-section">
          <h2 className="signup-page__form-title">Create an account</h2>

          {error && (
            <div className="signup-page__error" role="alert">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="signup-page__form">
            <div className="signup-page__field">
              <label className="signup-page__label">Email</label>
              <div className="signup-page__input-wrap">
                <Mail className="signup-page__input-icon" aria-hidden />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="signup-page__input"
                  placeholder="Enter your email"
                />
              </div>
            </div>

            <div className="signup-page__field">
              <label className="signup-page__label">Password</label>
              <div className="signup-page__input-wrap">
                <Lock className="signup-page__input-icon" aria-hidden />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="signup-page__input"
                  placeholder="Choose a password"
                />
              </div>
            </div>

            <div className="signup-page__field">
              <label className="signup-page__label">Confirm password</label>
              <div className="signup-page__input-wrap">
                <Lock className="signup-page__input-icon" aria-hidden />
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="signup-page__input"
                  placeholder="Confirm password"
                />
              </div>
            </div>

            <div className="signup-page__field">
              <label className="signup-page__label">Access code</label>
              <div className="signup-page__input-wrap">
                <Lock className="signup-page__input-icon" aria-hidden />
                <input
                  type="text"
                  value={accessCode}
                  onChange={(e) => setAccessCode(e.target.value)}
                  required
                  className="signup-page__input"
                  placeholder="Enter access code"
                />
              </div>
              <p className="signup-page__hint">Contact administrator for access code.</p>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="signup-page__submit"
            >
              {loading ? (
                <span className="signup-page__spinner" aria-hidden />
              ) : (
                <>
                  <UserPlus className="signup-page__submit-icon" aria-hidden />
                  Sign Up
                </>
              )}
            </button>
          </form>

          <p className="signup-page__signin">
            Already have an account? <Link to="/signin" className="signup-page__link">Sign in</Link>
          </p>
        </section>
      </div>
    </div>
  );
};

export default SignUpPage;
